import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Input,
  InputGroup,
  InputLeftElement,
  Box,
  Flex,
  Avatar,
  Text,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { FiSearch } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { setActiveProspect } from '../../redux/actions/conversationActions';
import axios from '../../utils/axios';

const NewRoomDialog = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `/api/chatroom/search?q=${encodeURIComponent(query)}`,
        );
        setResults(res.data.data || []);
      } catch (error) {
        console.error('Search error:', error.message);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [query]);

  // Reset search when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
    }
  }, [isOpen]);

  const handleSelectProspect = useCallback(
    async (prospect) => {
      try {
        // Check if a room already exists for this prospect, else create one
        //const res = await axios.get(`/api/chatroom/single?id=${prospect._id}`);

        //setResults(res.data.data || []);

        // Set this as the active prospect/room so ThreadCompose + thread view load it
        dispatch(setActiveProspect(prospect));

        onClose();
      } catch (error) {
        console.error('Failed to open chat room:', error.message);
      }
    },
    [dispatch, onClose],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px" fontWeight={500}>
          New chat
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          <InputGroup mb={3}>
            <InputLeftElement pointerEvents="none">
              <FiSearch color="gray" />
            </InputLeftElement>
            <Input
              placeholder="Search by name, email, phone, or company"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </InputGroup>

          <Box maxHeight="320px" overflowY="auto">
            {loading && (
              <Center py={6}>
                <Spinner size="sm" />
              </Center>
            )}

            {!loading && query.trim() && results.length === 0 && (
              <Text fontSize="14px" color="gray.500" textAlign="center" py={6}>
                No prospects found
              </Text>
            )}

            {!loading &&
              results.map((prospect) => (
                <Flex
                  key={prospect._id}
                  align="center"
                  gap={3}
                  px={2}
                  py={2}
                  borderRadius="md"
                  cursor="pointer"
                  _hover={{ background: 'orange' }}
                  onClick={() => handleSelectProspect(prospect)}
                >
                  <Avatar
                    size="sm"
                    name={prospect.fullName || prospect.firstName}
                    src={prospect.imageUrl}
                  />
                  <Box flex={1} minWidth={0}>
                    <Text fontSize="14px" fontWeight={500} noOfLines={1}>
                      {prospect.fullName ||
                        `${prospect.firstName} ${prospect.lastNname}`}
                    </Text>
                    <Text fontSize="12px" color="gray.500" noOfLines={1}>
                      {prospect.currentPosition}
                      {prospect.companyInfo?.companyName &&
                        ` · ${prospect.companyInfo.companyName}`}
                    </Text>
                  </Box>
                </Flex>
              ))}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
export default NewRoomDialog;
